import type { CryptoSwapsTranslations } from "../interfaces/cryptoSwaps";

export const cryptoSwapsTranslations: CryptoSwapsTranslations = {
  // Page chrome
  page_title: "Buy USDT",
  page_description: "Buy USDT with your COP balance and receive it in your wallet.",
  page_breadcrumb_wallets: "Wallets",
  page_history_link: "View history",
  page_new_order: "New purchase",

  // Organization (staff)
  org_picker_label: "Organization",
  org_picker_placeholder: "Search by name, email or ID",
  org_picker_loading: "Loading organizations…",
  org_picker_empty: "No organization matches",
  org_picker_searching: "Searching…",
  org_picker_search_error: "We could not search organizations. Try again.",
  org_picker_catalog_error: "We could not load the organizations.",
  org_picker_catalog_truncated: "Showing the first organizations. Type to search the rest.",
  org_picker_required_hint: "Choose the organization whose wallet is debited.",
  org_picker_org_hint: "The purchase is debited from this organization's wallet.",
  org_picker_global_option: "All organizations",
  org_picker_global_hint: "Showing purchases from every organization.",
  org_picker_filter_hint: "Showing only this organization's purchases.",

  // Availability
  pairs_load_failed: "We could not load USDT buying",
  pairs_retry: "Retry",
  pairs_empty: "Buying USDT is not enabled for your account. Talk to your account manager to enable it.",
  pair_label: "Asset to buy",
  pair_unavailable_title: "Buying is not available right now",
  pair_unavailable_not_configured: "Buying {{asset}} is not enabled for your organization yet.",
  pair_unavailable_disabled: "Buying {{asset}} is temporarily paused. Try again later.",
  pair_unavailable_generic: "Buying {{asset}} is not available right now.",
  pair_unavailable_breb_key:
    "Buying {{asset}} requires an active Bre-B key for your organization: if a purchase cannot be completed, the pesos are refunded to it.",
  pair_breb_key_create: "Create Bre-B key",

  // Amounts
  amount_section_title: "How much do you want to buy?",
  amount_send_label: "You pay",
  amount_receive_label: "You receive",
  amount_send_source: "From your Coloca balance",
  amount_pricing: "Calculating the price…",
  amount_invalid: "Enter an amount greater than zero.",
  amount_precision: "Use at most {{decimals}} decimals.",
  amount_debit_note: "{{amount}} {{currency}} is debited because of price rounding.",
  price_line: "1 {{asset}} = {{price}} {{currency}}",
  amount_below_min: "The minimum per purchase is {{amount}} {{currency}}.",
  amount_above_max: "The maximum per purchase is {{amount}} {{currency}}.",
  amount_out_of_range: "This amount is outside the per-purchase limits ({{min}} – {{max}} {{currency}}).",
  send_limits_hint: "Min. {{min}} {{currency}} · Max. {{max}} {{currency}} per purchase",
  send_min_hint: "Min. {{min}} {{currency}} per purchase",
  send_max_hint: "Max. {{max}} {{currency}} per purchase",
  receive_limits_hint: "If you set how much to receive: min. {{min}} · max. {{max}} {{currency}}",
  receive_min_hint: "If you set how much to receive: min. {{min}} {{currency}}",
  receive_max_hint: "If you set how much to receive: max. {{max}} {{currency}}",
  receive_below_min: "To set how much to receive, the minimum is {{amount}} {{currency}}.",
  receive_above_max: "To set how much to receive, the maximum is {{amount}} {{currency}}.",
  receive_out_of_range:
    "To set how much to receive, the amount must be between {{min}} and {{max}} {{currency}}.",
  balance_available: "Available: {{amount}} {{currency}}",
  balance_loading: "Checking balance…",
  balance_error: "We could not check the balance.",
  balance_missing: "This organization has no {{currency}} balance.",
  balance_insufficient: "Insufficient balance · Available: {{amount}} {{currency}}",
  balance_pick_org: "Choose an organization to see its balance.",

  // Destination
  destination_title: "Which wallet should we send your {{asset}} to?",
  destination_network_label: "Network",
  destination_network_only: "Only network available",
  destination_address_label: "Wallet address",
  destination_address_placeholder: "0x…",
  destination_address_empty_hint: "Paste the address of the receiving wallet.",
  destination_address_valid: "Valid {{network}} address.",
  destination_address_invalid: "This is not a valid {{network}} address. Check it is complete and correct.",
  destination_network_unsupported: "We cannot verify addresses on this network.",
  destination_onchain_warning:
    "Make sure the wallet accepts {{asset}} on {{network}}. If the network does not match, the funds are lost.",
  destination_compare: "Compare the first and last 6 characters with your wallet.",
  concept_label: "Concept (optional)",
  concept_placeholder: "E.g. Treasury top-up",
  concept_too_long: "Use at most {{max}} characters.",

  // Summary ticket
  quote_ticket_title: "Summary",
  quote_valid_for: "Price valid for {{time}}",
  quote_expired_badge: "Price expired",
  quote_pricing_badge: "Calculating…",
  quote_no_price_badge: "No price",
  quote_timer_label: "Time left on the price",
  quote_announce_seconds: "{{seconds}} seconds left on this price.",
  quote_announce_expired: "The price expired.",
  quote_expired_title: "The price expired",
  quote_expired_message: "Get a new price to continue. No money moved.",
  quote_expired_action: "Get a new price",
  quote_retry_action: "Try again",
  quote_manual_title:
    "Pricing failed several times in a row. Check the amount and ask for a price when ready.",
  quote_manual_action: "Get price",
  quote_summary_price: "Price",
  quote_summary_pay: "You pay",
  quote_summary_receive: "You receive",
  quote_summary_fees: "Fees",
  quote_summary_network: "Network",
  fees_included: "Included in the price",
  quote_submit: "Review purchase",
  quote_submit_footnote:
    "Your Coloca balance is debited when you confirm. If delivery fails, we return everything debited.",

  // Review blocks
  block_readonly: "Your role can view this section but not buy.",
  block_organization: "Choose the organization before pricing.",
  block_unavailable: "Buying is not available right now.",
  block_empty: "Enter how much you want to pay or receive.",
  block_amount: "Fix the amount to see the price.",
  block_pricing: "Calculating the price…",
  block_unpriced: "No price yet. Ask for it again to continue.",
  block_expired: "The price expired. Get a new one.",
  block_balance: "Your Coloca balance does not cover this purchase.",
  block_destination: "Enter a valid destination address.",

  // Confirm dialog
  confirm_title: "Confirm your purchase",
  confirm_headline: "You are buying {{receive}} for {{send}}",
  confirm_price: "Price",
  confirm_fees: "Fees",
  confirm_network: "Network",
  confirm_concept: "Concept",
  confirm_destination_title: "Destination wallet",
  confirm_checksum_ok: "Valid format",
  confirm_acknowledge:
    "I checked the address and confirm it is a {{network}} wallet. I understand the transfer cannot be reversed.",
  confirm_expired_title: "The price expired",
  confirm_expired_message: "Refresh it to buy at the current price. No money moved.",
  confirm_refresh: "Refresh price",
  confirm_back: "Back",
  confirm_create: "Buy {{asset}}",
  confirm_network_warning_title: "{{network}} only",
  confirm_network_warning:
    "Double-check that the destination wallet is on {{network}}. If the address belongs to another network (Ethereum, Tron, Solana, Polygon…), the funds are lost and cannot be recovered.",
  confirm_saved_wallet: "Saved wallet: {{alias}}",
  confirm_will_save_wallet: "It will be saved as “{{alias}}” for future purchases.",
  saved_wallets_label: "Destination wallet",
  saved_wallets_new: "New address",
  saved_wallets_manage: "Manage saved",
  saved_wallets_title: "Saved wallets",
  saved_wallets_description: "Destination addresses your organization saved for future purchases.",
  saved_wallets_empty: "There are no saved wallets.",
  saved_wallets_delete: "Delete",
  saved_wallets_delete_confirm: "Delete this saved wallet?",
  saved_wallets_delete_failed: "We could not delete the wallet. Try again.",
  saved_wallet_save_label: "Save this wallet for future purchases",
  saved_wallet_save_hint: "We will ask for your authentication code (2FA) when you confirm.",
  saved_wallet_alias_label: "Name (optional)",
  saved_wallet_alias_placeholder: "E.g. Treasury Binance",
  saved_wallet_alias_too_long: "Use at most {{max}} characters for the name.",
  saved_wallet_ownership_label: "Who owns this wallet?",
  saved_wallet_ownership_own: "My organization",
  saved_wallet_ownership_third_party: "A third party",
  saved_wallet_holder_label: "Wallet holder",
  saved_wallet_holder_placeholder: "Name or company name",
  saved_wallet_holder_required: "Enter the wallet holder.",
  saved_wallet_declaration:
    "By saving it you declare who controls this wallet. The declaration is recorded with your user.",
  saved_wallet_owner_own: "Your organization's",
  saved_wallet_owner_third_party: "Third party: {{holder}}",
  saved_wallet_two_factor_title: "Confirm the wallet to save",
  saved_wallet_two_factor_description:
    "Enter the code from your authenticator app to save the wallet and continue with the purchase.",
  saved_wallet_two_factor_required:
    "To save destination wallets you need to turn on two-factor authentication (2FA).",
  order_creating_title: "Creating your purchase…",
  order_creating_hint: "Do not close this window.",
  transit_wallet: "{{currency}} wallet",
  transit_your_wallet: "Your wallet",
  transit_breb_key: "Your Bre-B key",
  transit_buy_aria:
    "Your {{send}} leave your wallet, Coloca turns them into {{receive}} and they travel over {{network}} to your wallet.",
  transit_sell_aria:
    "Your {{send}} arrive over {{network}}, Coloca turns them into {{receive}} and pays them to your Bre-B key.",

  // Errors
  error_unconfirmed:
    "We could not confirm whether the purchase was created. Check the history before trying again, so you do not buy twice.",
  error_unconfirmed_history: "Check history",
  error_network: "No connection. Check your network and try again.",
  error_service_unavailable: "The service is not available right now. Try again in a few minutes.",
  error_price_unavailable: "We could not get a price right now. Try again in a few seconds.",
  error_verification_required: "Your organization must complete its verification before buying.",
  error_forbidden: "You do not have permission for this operation.",
  error_breb_key_required:
    "To buy crypto your organization needs an active Bre-B key: refunds are sent to it.",
  error_otc_not_enabled: "Crypto is not active for your organization. Contact support to activate it.",
  error_conflict: "This quote was already used or the purchase is still processing. Check the history.",
  error_not_found: "We could not find what you are looking for.",
  error_quote_expired: "The price expired before you confirmed. Here is a new one.",
  error_rejected: "We could not process the request. Check the details and try again.",
  error_unknown: "Something went wrong. Try again.",

  // Statuses
  status_created: "Created",
  status_processing: "Processing",
  status_paid: "Delivered",
  status_failed: "Failed",
  status_cancelled: "Cancelled",
  status_reversed: "Reversed",
  filter_all: "All",

  // History
  history_title: "Purchase history",
  history_description: "Your USDT purchases, with their delivery status and each transaction hash.",
  history_export: "Export CSV",
  history_export_empty: "There are no purchases to export with these filters.",
  history_load_error: "We could not load the history.",
  history_refresh: "Refresh",
  history_count_one: "{{count}} purchase",
  history_count_other: "{{count}} purchases",
  history_filter_group: "Filter by status",
  history_range_label: "Period",
  history_range_placeholder: "Any date",

  // Table
  table_col_date: "Date",
  table_col_operation: "Purchase",
  table_col_destination: "Destination",
  table_col_status: "Status",
  table_empty_title: "No purchases yet",
  table_empty_description: "When you buy USDT, it shows up here.",
  table_no_results_title: "No purchase matches",
  table_no_results_description: "Try another status or period.",

  // Drawer
  drawer_title: "USDT purchase",
  drawer_created_updated: "Created {{created}} · Updated {{updated}}",
  drawer_open_order_page: "Open purchase",
  drawer_not_found:
    "We could not find that purchase. It may belong to another organization or the link is incomplete.",
  drawer_not_found_dismiss: "Dismiss notice",

  // Summary
  summary_title: "Details",
  summary_debited: "Debited",
  summary_received: "Delivered",
  summary_price: "Price",
  summary_fees: "Fees",
  summary_network: "Network",
  summary_address: "Address",
  summary_tx_hash: "Transaction hash",
  summary_tx_pending: "Available once sent",
  eta_label: "Estimated time",
  eta_short: "≈ {{minutes}} min",
  eta_estimate: "Estimated time: ≈ {{minutes}} min",
  eta_running: "In progress",
  eta_remaining: "~{{time}} left",
  eta_elapsed: "{{time}} elapsed",
  eta_progress_label: "Progress against the estimated time",
  eta_late: "It's taking a bit longer than estimated. It's still in progress; you don't need to do anything.",
  eta_trend_slower: "Operations are taking longer than usual today.",
  eta_trend_slower_short: "Slower than usual",
  summary_view_explorer: "View on {{explorer}}",
  summary_concept: "Concept",
  summary_created: "Created",
  summary_paid_at: "Delivered",
  summary_failed_at: "Failed",
  summary_order_id: "Purchase ID",

  // Order page
  order_breadcrumb: "USDT purchase",
  order_not_found_title: "We could not find this purchase",
  order_not_found_description: "It may belong to another organization or the link is incomplete.",
  order_back_to_history: "Go to history",
  order_headline_live: "Sending {{amount}}",
  order_headline_paid: "{{amount}} delivered",
  order_headline_failed: "We could not deliver {{amount}}",
  order_subtitle: "To {{address}} on {{network}}.",
  order_live_title: "Updating live",
  order_live_updated: "Last updated {{time}}",
  order_live_stale: "We could not refresh the status. What you see may be out of date.",
  order_view_history: "View history",
  outcome_live_title: "On its way",
  outcome_live_message:
    "We are sending your {{currency}} on {{network}}. This page updates on its own; the hash appears once the transaction is out.",
  outcome_paid_title: "Delivered",
  outcome_failed_title: "Could not be delivered",
  outcome_failed_message:
    "The transfer did not complete. We returned everything debited, fees included, to your Coloca balance.",
  outcome_failed_returned: "Returned to your Coloca balance",
  outcome_to_address: "To the address on {{network}}",

  // Timeline
  timeline_title: "Tracking",
  timeline_created: "Purchase created",
  timeline_processing: "Sending",
  timeline_processing_hint: "The transaction is in progress.",
  timeline_delivered: "Delivered",
  timeline_failed: "Failed and refunded",
  timeline_state_done: "completed",
  timeline_state_current: "in progress",
  timeline_state_todo: "pending",
  timeline_state_failed: "failed",

  // CSV
  csv_filename: "usdt-purchases",
  csv_col_id: "ID",
  csv_col_created_at: "Created at",
  csv_col_status: "Status",
  csv_col_send_amount: "Amount debited",
  csv_col_send_currency: "Debited currency",
  csv_col_receive_amount: "Amount delivered",
  csv_col_receive_currency: "Delivered currency",
  csv_col_price: "Price",
  csv_col_fee_conversion: "Conversion fee",
  csv_col_fee_delivery: "Delivery fee",
  csv_col_network: "Network",
  csv_col_address: "Destination address",
  csv_col_tx_hash: "Transaction hash",
  csv_col_concept: "Concept",
  csv_col_paid_at: "Delivered at",
  csv_col_failed_at: "Failed at",

  // Copy
  copy_done: "{{field}} copied",
  copy_failed: "We could not copy. Select the text and copy it manually.",
  copy_address: "Address",
  copy_tx_hash: "Transaction hash",
  copy_order_id: "Purchase ID",

  // Sell — page chrome
  sell_page_title: "Sell USDT",
  sell_page_description: "Send USDT from your wallet and receive pesos in your COP wallet.",
  sell_page_new: "New sale",
  sell_org_picker_required_hint: "Pick the organization that sells.",
  sell_org_picker_org_hint: "The pesos are credited to this organization's COP wallet.",

  // Sell — availability
  sell_pairs_load_failed: "We couldn't load USDT sales",
  sell_pairs_empty:
    "Selling USDT isn't enabled for your account. Talk to your account manager to turn it on.",
  sell_unavailable_title: "Selling is unavailable right now",
  sell_unavailable_not_configured: "Selling {{asset}} isn't enabled for your organization yet.",
  sell_unavailable_disabled: "Selling {{asset}} is temporarily paused. Try again later.",
  sell_unavailable_generic: "Selling {{asset}} isn't available right now.",
  sell_unavailable_breb_key:
    "To sell {{asset}} your organization needs an active Bre-B key: the pesos from the sale are paid to it.",

  // Sell — amounts
  sell_amount_title: "How much do you want to sell?",
  sell_amount_send_label: "You sell",
  sell_amount_receive_label: "Paid to your Bre-B key",
  sell_send_limits_hint: "Min. {{min}} {{currency}} · Max. {{max}} {{currency}} per sale",
  sell_send_min_hint: "Min. {{min}} {{currency}} per sale",
  sell_send_max_hint: "Max. {{max}} {{currency}} per sale",
  sell_amount_below_min: "The minimum sale is {{amount}} {{currency}}.",
  sell_amount_above_max: "The maximum sale is {{amount}} {{currency}}.",
  sell_amount_out_of_range: "This amount is outside the per-sale limits ({{min}} – {{max}} {{currency}}).",

  // Sell — network
  buy_network_title: "Which network do you want to receive on?",
  buy_network_hint:
    "Pick the network of the receiving wallet: if the wallet isn't on that network, the USDT doesn't arrive.",
  access_check_failed: "We couldn't check whether Crypto is active for your organization.",
  access_check_retry: "Retry",
  saved_wallet_default_alias: "Wallet {{start}}…{{end}}",
  sell_network_title: "Which network are you sending on?",
  sell_network_hint: "Pick the network your wallet uses: on another one the transfer doesn't arrive.",
  sell_network_option: "{{standard}} · network fee in {{gas}}",

  // Sell — wallet the deposit comes from
  sell_refund_title: "Which wallet do we return the USDT to?",
  sell_refund_hint: "If the sale doesn't complete, we return the {{asset}} to this wallet.",
  sell_refund_saved_label: "Your wallet",
  sell_refund_address_label: "Your wallet address",
  sell_refund_address_empty_hint:
    "Paste your wallet address on this network. If you send from an exchange, it can be your deposit address at that exchange.",
  sell_refund_warning:
    "Sending from an exchange? Add its withdrawal fee: exactly the sale amount has to arrive, or the sale may not complete.",
  sell_refund_save_label: "Save this wallet for future sales",
  sell_saved_wallet_two_factor_description:
    "Enter the code from your authenticator app to save the wallet and continue with the sale.",

  // Sell — key the pesos are paid to
  sell_payout_title: "The pesos are paid to your primary Bre-B key",
  sell_payout_note: "And credited to your Coloca COP wallet.",

  // Sell — summary ticket
  sell_ticket_net: "Reaches your COP wallet",
  sell_ticket_send: "You send",
  sell_ticket_proceeds: "Paid to your Bre-B key",
  sell_ticket_fee: "Fee",
  sell_ticket_fee_none: "No fee",
  sell_submit: "Review sale",
  sell_submit_footnote:
    "Nothing moves until you send the USDT. If the sale doesn't complete, we return it to your wallet.",

  // Sell — review blocks
  sell_block_readonly: "Your role can view this section but not sell.",
  sell_block_unavailable: "Selling isn't available right now.",
  sell_block_empty: "Enter how much you want to sell or receive.",
  sell_block_refund: "Enter the address of the wallet we return the USDT to.",

  // Sell — confirm dialog
  sell_confirm_title: "Confirm your sale",
  sell_confirm_headline: "You're selling {{send}} to receive {{receive}}",
  sell_confirm_rules_title: "How to send the USDT",
  sell_rule_network_title: "Only {{asset}} on {{network}}",
  sell_rule_network: "{{standard}} token; the network fee is paid in {{gas}}. Contract:",
  sell_rule_exact_title: "Exactly {{amount}}",
  sell_rule_exact: "In a single transfer. A different amount may keep the sale from completing.",
  sell_rule_wallet_title: "From an exchange? Add its fee",
  sell_rule_wallet:
    "The exchange takes its withdrawal fee from the amount: withdraw enough for exactly {{amount}} to arrive.",
  sell_confirm_refund_title: "Refund wallet",
  sell_confirm_network_warning:
    "Send only {{asset}} on {{network}}. If you send another coin or use another network (Ethereum, Tron, Solana…), the funds may be lost.",
  sell_confirm_acknowledge: "I will make exactly {{amount}} arrive on {{network}}, in a single transfer.",
  sell_confirm_create: "Create sale",
  sell_confirm_will_save_wallet: "It will be saved as “{{alias}}” for future sales.",
  sell_creating_title: "Creating your sale…",

  // Sell — errors
  sell_error_unconfirmed:
    "We couldn't confirm whether the sale was created. Check your sales: if it's there, send to the address it shows; if not, get a new price.",
  sell_error_not_created: "We couldn't create the sale and nothing moved. Here's a new price to try again.",
  sell_error_conflict:
    "This quote was already used, or you already have a sale awaiting its deposit. Check your sales.",
  sell_error_verification_required: "Your organization must complete its verification before selling.",
  sell_error_breb_key_required:
    "To sell crypto your organization needs an active Bre-B key: the pesos are paid to it.",
  sell_error_history: "View my sales",

  // Sell — statuses
  sell_status_awaiting_deposit: "Awaiting deposit",
  sell_status_processing: "Processing",
  sell_status_cop_sent: "Pesos sent",
  sell_status_completed: "Credited",
  sell_status_expired: "Expired",
  sell_status_refunding: "Refunding",
  sell_status_refunded: "Refunded",

  // Sell — sale page
  sell_order_breadcrumb: "USDT sale",
  sell_order_not_found_title: "We couldn't find this sale",
  sell_headline_awaiting: "Send {{amount}}",
  sell_headline_processing: "We received your {{asset}}",
  sell_headline_cop_sent: "Your pesos are on their way",
  sell_headline_completed: "We credited {{amount}}",
  sell_headline_expired: "The sale expired",
  sell_headline_refunding: "We're returning your {{asset}}",
  sell_headline_refunded: "We returned your {{asset}}",
  sell_subtitle: "The pesos are paid to your key {{key}} and credited to your COP wallet.",

  // Sell — deposit slip
  sell_deposit_title: "Send your USDT to this address",
  sell_deposit_qr_alt: "QR code of the deposit address on {{network}}",
  sell_deposit_amount: "Exact amount",
  sell_deposit_address: "Deposit address",
  sell_deposit_network: "Network",
  sell_deposit_contract: "{{asset}} contract:",
  sell_deposit_copy_amount: "Copy amount",
  sell_deposit_copy_address: "Copy address",
  sell_view_on_explorer: "View on {{explorer}}",
  sell_deposit_steps_title: "How to send it from MetaMask",
  sell_deposit_step_network: "Switch MetaMask to the {{network}} network.",
  sell_deposit_step_send: "Pick {{asset}}, tap Send and paste the deposit address.",
  sell_deposit_step_amount: "Enter exactly {{amount}} and confirm. You need {{gas}} for the network fee.",
  sell_deposit_step_wait: "Come back here: this page updates itself when your deposit arrives.",
  sell_deposit_warning:
    "Only {{asset}} on {{network}}, in a single transfer and from your wallet. Another coin, another network, a different amount, or a late or split transfer is not matched to your sale and is not returned automatically: recovering it is a manual process with support.",
  sell_deposit_window:
    "You have 15 minutes. This address is for this sale only: don't save or reuse it, since it is later assigned to other operations and a late or repeated deposit is not matched to your sale.",
  sell_deposit_time_left: "Expires in {{time}}",
  sell_deposit_expired_title: "Time to deposit is up",
  sell_deposit_expired_text:
    "Don't send anything to this address: it is no longer tied to your sale. If you already sent it, wait here: this page updates on its own.",
  sell_deposit_unavailable_title: "Deposit address not available",
  sell_deposit_unavailable_text:
    "Don't send anything yet: we can't show a valid address for this sale right now. This page updates on its own.",
  sell_deposit_tx_link: "View deposit on {{explorer}}",
  copy_deposit_amount: "Amount",
  copy_deposit_address: "Deposit address",
  copy_sale_id: "Sale ID",

  // Sell — outcome
  sell_outcome_processing_title: "Deposit received",
  sell_outcome_processing_message:
    "We received your {{asset}}. We're paying the pesos to your Bre-B key; this page updates itself.",
  sell_outcome_cop_sent_title: "Pesos sent",
  sell_outcome_cop_sent_message:
    "We paid {{amount}} to your key {{key}}. We'll credit it to your COP wallet in a few minutes.",
  sell_outcome_completed_title: "Credited",
  sell_outcome_completed_message: "We credited {{amount}} to your COP wallet.",
  sell_outcome_wallet_link: "Go to wallets",
  sell_outcome_expired_title: "The sale expired",
  sell_outcome_expired_message:
    "We didn't receive your deposit in time. Don't send {{asset}} to that address. If you already sent it, we keep checking it: if it arrives, the sale continues or we return it to your wallet.",
  sell_outcome_refunding_title: "Refund in progress",
  sell_outcome_refunding_to: "We're returning your {{asset}} to",
  sell_outcome_refunded_title: "We returned your {{asset}}",
  sell_outcome_refunded_to: "We returned your {{asset}} to",

  // Sell — why a sale did not complete
  sell_reason_payment_timeout: "We didn't receive the deposit in time.",
  sell_reason_compliance: "The deposit didn't pass the compliance review.",
  sell_reason_limit: "The amount exceeds the operation's limits.",
  sell_reason_payout_failed: "We couldn't pay the pesos to your Bre-B key.",
  sell_reason_refund_pending: "We're arranging the refund to your wallet.",
  sell_reason_generic: "The sale couldn't be completed.",

  // Sell — timeline
  sell_timeline_created: "Sale created",
  sell_timeline_awaiting: "Awaiting your deposit",
  sell_timeline_received: "Deposit received",
  sell_timeline_paying: "Paying the pesos to your key",
  sell_timeline_cop_sent: "Pesos paid to your key",
  sell_timeline_crediting: "Crediting your COP wallet",
  sell_timeline_credited: "Credited to your COP wallet",
  sell_timeline_expired: "Expired without a deposit",
  sell_timeline_refunding: "Returning to your wallet",
  sell_timeline_refunded: "Returned to your wallet",

  // Sell — summary
  sell_summary_deposit: "Deposit",
  sell_summary_proceeds: "Paid to your Bre-B key",
  sell_summary_fee: "Fee",
  sell_summary_credited: "Credited",
  sell_summary_pending: "Pending",
  sell_summary_deposit_tx: "Deposit hash",
  sell_summary_deposit_pending: "Available once it arrives",
  sell_summary_payout_key: "Bre-B key",
  sell_summary_refund_wallet: "Refund wallet",
  sell_summary_completed_at: "Credited",
  sell_summary_expired_at: "Expired",
  sell_summary_refunded_at: "Refunded",
  sell_summary_refund_tx: "Refund transaction",
  sell_refund_tx_link: "View refund on {{explorer}}",
  sell_summary_sale_id: "Sale ID",

  // History — purchases and sales
  history_hub_title: "History",
  history_hub_description: "Your USDT purchases and sales, with their status and each transaction's hash.",
  history_tab_buys: "Purchases",
  history_tab_sells: "Sales",
  sell_history_count_one: "{{count}} sale",
  sell_history_count_other: "{{count}} sales",
  sell_history_export_empty: "There are no sales to export with these filters.",
  sell_history_load_error: "We couldn't load your sales.",
  sell_table_col_operation: "Sale",
  sell_table_empty_title: "No sales yet",
  sell_table_empty_description: "When you sell USDT, it will show up here.",
  sell_table_no_results_title: "No sale matches",
  sell_drawer_title: "USDT sale",
  sell_drawer_open: "Open sale",
  sell_drawer_not_found:
    "We couldn't find that sale. It may belong to another organization or the link is incomplete.",

  // Sell — CSV
  sell_csv_filename: "usdt-sales",
  sell_csv_col_deposit_amount: "Deposited amount",
  sell_csv_col_deposit_currency: "Deposited currency",
  sell_csv_col_deposit_tx: "Deposit hash",
  sell_csv_col_proceeds: "Paid to the key",
  sell_csv_col_fee: "Fee",
  sell_csv_col_credited: "Credited",
  sell_csv_col_currency: "Credited currency",
  sell_csv_col_payout_key: "Bre-B key",
  sell_csv_col_refund_wallet: "Refund wallet",
  sell_csv_col_completed_at: "Credited at",
  sell_csv_col_refunded_at: "Refunded at",
  // Volume discount
  volume_title: "The more you buy, the cheaper the dollar",
  volume_meter_label: "Volume discount",
  volume_meter_saving: "You pay {{amount}} less per dollar than on the minimum purchase.",
  volume_meter_minimum: "The price per dollar drops as you buy more.",
  volume_meter_best: "You already have practically the best price.",
  volume_suggestion_amount: "Buy {{amount}}",
  volume_suggestion_wallet: "Buy your whole balance · {{amount}}",
  volume_suggestion_max: "Buy the maximum · {{amount}}",
  volume_suggestion_price: "{{price}} per dollar · {{savings}} less",
  volume_suggestion_price_only: "{{price}} per dollar",
  volume_suggestion_extra: "+{{amount}} extra",
  volume_top_up: "With {{amount}} you would pay {{price}} per dollar.",
  volume_top_up_action: "Top up wallet",
  volume_insufficient_title: "You don't have enough balance",
  volume_insufficient_text:
    "With {{amount}} you would get a discount of {{savings}} per dollar over the minimum purchase.",
  volume_insufficient_wallet: "Buy with your whole balance · {{amount}}",
  volume_sell_title: "The more you sell, the more pesos per dollar",
  volume_sell_meter_saving: "You get {{amount}} more per dollar than on the minimum sale.",
  volume_sell_meter_minimum: "You get more pesos per dollar as you sell more.",
  volume_sell_suggestion_amount: "Sell {{amount}}",
  volume_sell_suggestion_price: "{{price}} per dollar · {{savings}} more",
};
